'use client'

import Image from 'next/image'
import { useTranslations } from 'next-intl'

export type FoodPhoto = { url: string; name: string }

type Props = {
  photos: FoodPhoto[]
}

/**
 * Cinta horizontal infinita de fotos de platillos (marquee). Pasa sola, en loop
 * continuo y sin saltos: el track contiene la lista DUPLICADA y se anima de 0 a
 * -50% (una copia completa). CSS puro → fluido en GPU, sin JS por frame.
 * - Pausa al pasar el cursor.
 * - Respeta prefers-reduced-motion (se detiene).
 * - Bordes con fade (mask) para un acabado premium.
 */
export function FoodCarousel({ photos }: Props) {
  const t = useTranslations('public.microsite')
  if (!photos.length) return null

  // Rellena hasta tener cuerpo suficiente para que la cinta nunca se vea vacía
  // aunque el local tenga pocas fotos, luego duplica para el loop perfecto.
  let filled = [...photos]
  while (filled.length < 8) filled = [...filled, ...photos]
  const loop = [...filled, ...filled]
  const durationSec = filled.length * 4

  return (
    <div className="mt-12">
      <div className="flex items-center justify-center gap-3 mb-6">
        <span className="h-px w-10" style={{ background: 'var(--ms-border)' }} />
        <p
          className="text-[11px] font-bold tracking-[0.2em] uppercase"
          style={{ color: 'var(--ms-accent)' }}
        >
          {t('ourDishes')}
        </p>
        <span className="h-px w-10" style={{ background: 'var(--ms-border)' }} />
      </div>

      <div
        className="relative overflow-hidden"
        style={{
          WebkitMaskImage:
            'linear-gradient(to right, transparent, #000 6%, #000 94%, transparent)',
          maskImage:
            'linear-gradient(to right, transparent, #000 6%, #000 94%, transparent)',
        }}
      >
        <div
          className="redy-food-track flex gap-4 w-max"
          style={{ animation: `redy-food-marquee ${durationSec}s linear infinite` }}
        >
          {loop.map((p, i) => (
            <div
              key={`${p.url}-${i}`}
              className="relative h-44 w-64 flex-shrink-0 overflow-hidden shadow-lg"
              style={{ borderRadius: 'var(--ms-radius)' }}
            >
              <Image
                src={p.url}
                alt={p.name}
                fill
                sizes="256px"
                className="object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
              <p className="absolute bottom-3 left-3 right-3 text-white font-bold text-sm drop-shadow truncate">
                {p.name}
              </p>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes redy-food-marquee {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        .redy-food-track:hover { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) {
          .redy-food-track { animation: none !important; }
        }
      `}</style>
    </div>
  )
}
